import { gql } from '@apollo/client';
import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { AspectRatioBox } from '@moda/om';
import { Image } from '../../../../Image';
import { VariantCellImageGalleryFragment } from '../../../../../generated/types/VariantCellImageGalleryFragment';
import { getActiveVariantBy } from '../../../../../lib/getActiveVariantBy';
import { compact } from '../../../../../lib/compact';
import './VariantCellImageGallery.scss';
import { useDuration, Crossfader } from '../../../../Crossfader';

export const DEFAULT_ALT_TEXT = 'Product thumbnail';
export const IMAGE_DURATION_MS = 2000;

export const VARIANT_CELL_IMAGE_GALLERY_VARIANT_FRAGMENT = gql`
  fragment VariantCellImageGalleryVariantFragment on VariantData {
    id
    primaryImageUrls: primary_image_urls {
      src: medium
    }
    alternateImageUrls: alternate_image_urls {
      src: medium
    }
  }
`;

export const VARIANT_CELL_IMAGE_GALLERY_FRAGMENT = gql`
  fragment VariantCellImageGalleryFragment on Variant {
    id
    name
    masterVariant: master_variants_data {
      id
      ...VariantCellImageGalleryVariantFragment
    }
    otherVariants: other_variants_data {
      id
      ...VariantCellImageGalleryVariantFragment
    }
  }
  ${VARIANT_CELL_IMAGE_GALLERY_VARIANT_FRAGMENT}
`;

interface Props {
  variant: VariantCellImageGalleryFragment;
  activeVariantId: string;
  active?: boolean;
}

export const VariantCellImageGallery: React.FC<Props> = ({
  variant,
  activeVariantId,
  active = false
}) => {
  const [ref, inView] = useInView({ triggerOnce: true });
  const { cursor } = useDuration({ duration: IMAGE_DURATION_MS, active });

  const activeVariant = useMemo(() => getActiveVariantBy(variant, 'id', activeVariantId), [
    activeVariantId,
    variant
  ]);

  const srcs = useMemo(
    () =>
      compact(
        compact([activeVariant.primaryImageUrls, ...activeVariant.alternateImageUrls]).map(
          ({ src }) => src
        )
      ),
    [activeVariant.alternateImageUrls, activeVariant.primaryImageUrls]
  );

  const [primarySrc, ...secondarySrcs] = srcs;

  return (
    <AspectRatioBox
      className={classNames('VariantCellImageGallery', {
        'VariantCellImageGallery--active': active
      })}
      maxHeight={540}
      maxWidth={340}
    >
      {inView && secondarySrcs.length > 0 && (
        <Crossfader className="VariantCellImageGallery__incoming" cursor={cursor}>
          {srcs.map(src => (
            <Image
              key={src}
              className="VariantCellImageGallery__incoming"
              src={src}
              alt={variant.name || DEFAULT_ALT_TEXT}
            />
          ))}
        </Crossfader>
      )}
      {inView && secondarySrcs.length === 0 && (
        <Image
          key={primarySrc}
          className="VariantCellImageGallery__incoming"
          src={primarySrc}
          alt={variant.name || DEFAULT_ALT_TEXT}
        />
      )}

      <Image
        ref={ref}
        className="VariantCellImageGallery__image"
        src={inView ? primarySrc : ''}
        alt={variant.name || DEFAULT_ALT_TEXT}
      />
    </AspectRatioBox>
  );
};
